import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, forkJoin, of, throwError } from 'rxjs';
import { catchError, finalize, map, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { ApiEndpointsService } from './api-endpoints.service';
import { ApiHttpService } from './api-http.service';

export interface AceNotificationData {
  body?: string | null;
  deepLink?: string | null;
  url?: string | null;
  route?: string | null;
  fundId?: string | null;
  taskId?: string | null;
  requestId?: string | null;
  eventId?: string | null;
  channelId?: string | null;
  [key: string]: unknown;
}

export interface AceNotification {
  id: number;
  read: boolean;
  sender: unknown | null;
  title: string;
  type: string;
  data: AceNotificationData;
  audit: {
    createdDate: number;
    modifiedDate: number;
  };
}

@Injectable({ providedIn: 'root' })
export class AceNotificationService {
  private readonly notificationsSubject = new BehaviorSubject<
    AceNotification[]
  >([]);
  private readonly unreadCountSubject = new BehaviorSubject<number>(0);
  private readonly loadingSubject = new BehaviorSubject<boolean>(false);
  private readonly errorSubject = new BehaviorSubject<string | null>(null);
  private refreshRequest = 0;
  private mutationVersion = 0;

  readonly notifications$ = this.notificationsSubject.asObservable();
  readonly unreadCount$ = this.unreadCountSubject.asObservable();
  readonly loading$ = this.loadingSubject.asObservable();
  readonly error$ = this.errorSubject.asObservable();

  constructor(
    private readonly apiHttpService: ApiHttpService,
    private readonly apiEndpointsService: ApiEndpointsService
  ) {}

  refresh(): Observable<void> {
    if (!environment.teamApiUrl?.trim()) {
      this.refreshRequest++;
      this.notificationsSubject.next([]);
      this.unreadCountSubject.next(0);
      this.errorSubject.next(null);
      return of(void 0);
    }

    const request = ++this.refreshRequest;
    const mutation = this.mutationVersion;
    this.loadingSubject.next(true);
    this.errorSubject.next(null);

    let listFailed = false;
    let countFailed = false;
    const list$ = this.apiHttpService
      .get(this.apiEndpointsService.getNotifications({ page: 0, size: 20 }))
      .pipe(
        map(response => this.unwrapNotifications(response)),
        catchError(() => {
          listFailed = true;
          return of([] as AceNotification[]);
        })
      );
    const count$ = this.apiHttpService
      .get(this.apiEndpointsService.getNotificationBadgeCount())
      .pipe(
        map(response => this.unwrapCount(response)),
        catchError(() => {
          countFailed = true;
          return of(null);
        })
      );

    return forkJoin({ notifications: list$, unreadCount: count$ }).pipe(
      tap(result => {
        if (
          request !== this.refreshRequest ||
          mutation !== this.mutationVersion
        ) {
          return;
        }
        this.notificationsSubject.next(result.notifications);
        if (result.unreadCount !== null) {
          this.unreadCountSubject.next(result.unreadCount);
        }
        this.errorSubject.next(
          listFailed || countFailed
            ? 'Notifications could not be fully refreshed.'
            : null
        );
      }),
      map(() => void 0),
      finalize(() => {
        if (request === this.refreshRequest) {
          this.loadingSubject.next(false);
        }
      })
    );
  }

  markAsRead(id: number): Observable<void> {
    return this.apiHttpService
      .patch(this.apiEndpointsService.markNotificationRead(id), {})
      .pipe(
        tap(() => {
          this.mutationVersion++;
          const current = this.notificationsSubject.value;
          const notification = current.find(item => item.id === id);
          if (!notification || notification.read) return;
          this.notificationsSubject.next(
            current.map(item =>
              item.id === id ? { ...item, read: true } : item
            )
          );
          this.unreadCountSubject.next(
            Math.max(0, this.unreadCountSubject.value - 1)
          );
        }),
        map(() => void 0),
        catchError(error => {
          this.errorSubject.next('Notification could not be marked as read.');
          return throwError(() => error);
        })
      );
  }

  markAllAsRead(): Observable<void> {
    return this.apiHttpService
      .patch(this.apiEndpointsService.markAllNotificationsRead(), {})
      .pipe(
        tap(() => {
          this.mutationVersion++;
          this.notificationsSubject.next(
            this.notificationsSubject.value.map(item => ({
              ...item,
              read: true,
            }))
          );
          this.unreadCountSubject.next(0);
        }),
        map(() => void 0),
        catchError(error => {
          this.errorSubject.next('Notifications could not be marked as read.');
          return throwError(() => error);
        })
      );
  }

  private unwrapNotifications(response: unknown): AceNotification[] {
    if (Array.isArray(response))
      return response
        .map(item => this.toNotification(item))
        .filter(item => item.id > 0);
    if (!response || typeof response !== 'object') return [];
    const envelope = response as { data?: unknown; items?: unknown };
    const value = Array.isArray(envelope.data) ? envelope.data : envelope.items;
    return Array.isArray(value)
      ? value.map(item => this.toNotification(item)).filter(item => item.id > 0)
      : [];
  }

  private toNotification(value: unknown): AceNotification {
    const item =
      value && typeof value === 'object'
        ? (value as Record<string, unknown>)
        : {};
    const rawData = item['data'];
    const data =
      rawData && typeof rawData === 'object' && !Array.isArray(rawData)
        ? (rawData as AceNotificationData)
        : {};
    const rawAudit = item['audit'];
    const audit =
      rawAudit && typeof rawAudit === 'object'
        ? (rawAudit as Record<string, unknown>)
        : {};
    return {
      id: Number(item['id']) || 0,
      read: item['read'] === true,
      sender: item['sender'] ?? null,
      title: typeof item['title'] === 'string' ? item['title'] : 'Notification',
      type: typeof item['type'] === 'string' ? item['type'] : 'GENERAL',
      data,
      audit: {
        createdDate: Number(audit['createdDate']) || 0,
        modifiedDate: Number(audit['modifiedDate']) || 0,
      },
    };
  }

  private unwrapCount(response: unknown): number {
    if (typeof response === 'number') return Math.max(0, response);
    if (!response || typeof response !== 'object') return 0;
    const value = response as {
      badgeCount?: unknown;
      unreadCount?: unknown;
      data?: unknown;
    };
    if (typeof value.data === 'number') return Math.max(0, value.data);
    const count = value.badgeCount ?? value.unreadCount;
    return typeof count === 'number' && Number.isFinite(count)
      ? Math.max(0, count)
      : 0;
  }
}
